import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { adsService } from '../../services/ads.service';
import LoadingState from '../../components/ui/LoadingState';

const { FiPlus, FiMegaphone, FiLayers, FiActivity, FiSettings } = FiIcons;

export default function AdManagement() {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadCampaigns();
  }, []);

  const loadCampaigns = async () => {
    try {
      setLoading(true);
      const data = await adsService.getCampaigns();
      setCampaigns(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <LoadingState />;

  return (
    <div className="max-w-7xl mx-auto px-4 py-12">
      <div className="flex justify-between items-center mb-12">
        <div>
          <h1 className="text-4xl font-black text-slate-900 tracking-tight">Ad Management</h1>
          <p className="text-slate-500 mt-2 font-medium">Coordinate sponsorship and healthcare awareness campaigns.</p>
        </div>
        <button className="px-6 py-3 bg-slate-900 text-white font-bold rounded-2xl hover:bg-slate-800 transition-all shadow-lg shadow-slate-900/10 flex items-center">
          <SafeIcon icon={FiPlus} className="mr-2" />
          Create Campaign
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
        <div className="bg-blue-600 p-8 rounded-[2.5rem] text-white shadow-xl shadow-blue-500/20">
          <SafeIcon icon={FiMegaphone} className="text-3xl mb-4" />
          <div className="text-4xl font-black">{campaigns.length}</div>
          <div className="text-blue-100 font-bold uppercase tracking-widest text-xs mt-1">Total Campaigns</div>
        </div>
        <div className="bg-slate-900 p-8 rounded-[2.5rem] text-white">
          <SafeIcon icon={FiLayers} className="text-3xl mb-4" />
          <div className="text-4xl font-black">
            {campaigns.reduce((acc, c) => acc + (c.ads?.length || 0), 0)}
          </div>
          <div className="text-slate-400 font-bold uppercase tracking-widest text-xs mt-1">Total Placements</div>
        </div>
        <div className="bg-white p-8 rounded-[2.5rem] border border-slate-100 shadow-sm">
          <SafeIcon icon={FiActivity} className="text-3xl mb-4 text-blue-600" />
          <div className="text-4xl font-black text-slate-900">
            {campaigns.filter(c => c.status === 'active').length}
          </div>
          <div className="text-slate-400 font-bold uppercase tracking-widest text-xs mt-1">Active Now</div>
        </div>
      </div>

      <div className="space-y-6">
        {campaigns.map(campaign => (
          <div key={campaign.id} className="bg-white rounded-[2rem] border border-slate-100 shadow-sm overflow-hidden p-8">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
              <div>
                <div className="flex items-center gap-3">
                  <h3 className="text-2xl font-black text-slate-900">{campaign.name}</h3>
                  <span className={`px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-tighter ${
                    campaign.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600'
                  }`}>
                    {campaign.status}
                  </span>
                </div>
                <p className="text-slate-500 font-bold text-sm mt-1">Sponsor: {campaign.sponsor_name}</p>
              </div>
              <div className="flex gap-2">
                <button className="p-3 bg-slate-50 text-slate-400 hover:text-slate-900 rounded-xl transition-all border border-slate-100">
                  <SafeIcon icon={FiSettings} />
                </button>
                <button className="px-4 py-2 bg-blue-50 text-blue-600 font-bold rounded-xl hover:bg-blue-100 transition-all text-sm">
                  Add Ad Unit
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {campaign.ads?.map(ad => (
                <div key={ad.id} className="p-6 bg-slate-50 rounded-2xl border border-slate-100 group hover:border-blue-200 transition-all">
                  <div className="flex justify-between items-start mb-4">
                    <span className="text-[10px] font-black uppercase text-slate-400 tracking-widest bg-white px-2 py-1 rounded-md border border-slate-100">
                      {ad.placement}
                    </span>
                    <div className={`w-2 h-2 rounded-full ${ad.is_active ? 'bg-green-500' : 'bg-slate-300'}`} />
                  </div>
                  <div className="text-sm font-bold text-slate-900 mb-1 truncate">{ad.destination_url}</div>
                  <div className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                    Priority: {ad.priority} • {ad.language_targeting || 'Global'}
                  </div>
                </div>
              ))}
              {(!campaign.ads || campaign.ads.length === 0) && (
                <div className="col-span-full py-8 text-center border-2 border-dashed border-slate-100 rounded-2xl text-slate-400 font-medium">
                  No ad units configured for this campaign.
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}